import { createRouter, createWebHistory } from 'vue-router'
import utils from '@/../../shared/utils'
import { save_pending_admin_route } from '@/utils/admin-redirect'
import Home from '../views/Home.vue'
import Auth from '../views/Auth.vue'
import Login from '../views/Login.vue'
import Lobby from '../views/Lobby.vue'
import Pause from '../views/Pause.vue'
import Interlude from '../views/Interlude.vue'
import EventsHome from '../views/admin/EventsHome.vue'
import EventStages from '../views/admin/EventStages.vue'
import StageRundown from '../views/admin/StageRundown.vue'
import EventRankings from '../views/admin/EventRankings.vue'
import EventParticipants from '../views/admin/EventParticipants.vue'
import EventSettings from '../views/admin/EventSettings.vue'
import Users from '../views/admin/Users.vue'
import Settings from '../views/admin/Settings.vue'
import End from '../views/End.vue'
import Title from '../views/Title.vue'
import Activity from '../views/Activity.vue'
import Result from '../views/Result.vue'
import Ranking from '../views/Ranking.vue'
import MySeasons from '../views/MySeasons.vue'
import MyStages from '../views/MyStages.vue'
import StageActivity from '../views/StageActivity.vue'
import Register from '../views/Register.vue'
import JoinSeason from '../views/JoinSeason.vue'

const routes = [
    {
        path: '/',
        name: 'Home',
        component: Home
    },
    //{
    //    path: '/test',
    //    name: 'Test',
    //    component: Test
    //},
    {
        // The server validates this invitation and binds the socket to its event.
        path: '/join/:event_code?',
        name: 'Auth',
        component: Auth
    },
    {
        path: '/login/:event_code?',
        name: 'Login',
        component: Login
    },
    {
        path: '/title',
        name: 'Title',
        component: Title
    },
    {
        path: '/activity',
        name: 'Activity',
        component: Activity
    },
    {
        path: '/result',
        name: 'Result',
        component: Result
    },
    // --- Admin panel (meta.admin: staff only, see the guard below and
    // client.js's handle_client). Hierarchy: events -> one event's
    // workspace (stages / rankings / participants / settings) -> one
    // stage's rundown.
    {
        path: '/panel',
        name: 'Panel',
        component: EventsHome,
        meta: { admin: true },
    },
    {
        path: '/panel/eventos/:season_id',
        name: 'Stages',
        component: EventStages,
        meta: { admin: true },
    },
    {
        path: '/panel/eventos/:season_id/etapas/:stage_id',
        name: 'StageRundown',
        component: StageRundown,
        meta: { admin: true },
    },
    {
        path: '/panel/eventos/:season_id/rankings',
        name: 'RankingsAdmin',
        component: EventRankings,
        meta: { admin: true },
    },
    {
        path: '/panel/eventos/:season_id/participantes',
        name: 'EventParticipants',
        component: EventParticipants,
        meta: { admin: true },
    },
    {
        path: '/panel/eventos/:season_id/configuracion',
        name: 'EventSettings',
        component: EventSettings,
        meta: { admin: true },
    },
    {
        path: '/panel/usuarios',
        name: 'Users',
        component: Users,
        meta: { admin: true },
    },
    {
        path: '/panel/ajustes',
        name: 'Settings',
        component: Settings,
        meta: { admin: true },
    },
    // Old admin URLs (bookmarks, links in docs).
    { path: '/users', redirect: { name: 'Users' } },
    { path: '/stages/:season_id', redirect: (to) => ({ name: 'Stages', params: to.params }) },
    { path: '/rankings-admin/:season_id', redirect: (to) => ({ name: 'RankingsAdmin', params: to.params }) },
    {
        path: '/end',
        name: 'End',
        component: End,
    },
    {
        path: '/lobby',
        name: 'Lobby',
        component: Lobby
    },
    {
        // GAME_STATES.PAUSED's screen - see Pause.vue and server.js's
        // _run_crossed_sectors_then_hold. client.js already routes both
        // PLAYER and MANAGER here on that state; this just supplies the
        // actual route/view (previously missing entirely).
        path: '/pause',
        name: 'Pause',
        component: Pause,
    },
    {
        // GAME_STATES.INTERLUDE's screen - the stage intro / "veamos cómo
        // ha ido" animations (server.js's _show_interlude).
        path: '/interlude',
        name: 'Interlude',
        component: Interlude,
    },
    {
        path: '/ranking',
        name: 'Ranking',
        component: Ranking,
    },
    {
        path: '/my-seasons',
        name: 'MySeasons',
        component: MySeasons,
    },
    {
        path: '/my-seasons/:season_id/stages',
        name: 'MyStages',
        component: MyStages,
    },
    {
        path: '/stage/:stage_id/play',
        name: 'StageActivity',
        component: StageActivity,
    },
    {
        path: '/register',
        name: 'Register',
        component: Register,
    },
    {
        // ONLINE mode's per-season equivalent of EVENT mode's '/join/:event_code'
        // above (that one is Auth.vue's websocket GUEST flow - entirely
        // unrelated). Public: a not-yet-registered guest needs to land here
        // and see which season this is before logging in/creating an account
        // - see JoinSeason.vue.
        path: '/join-season/:code',
        name: 'JoinSeason',
        component: JoinSeason,
    },
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
})

// Panel/Users assume an authenticated staff member's full game state (e.g.
// the unredacted correctAnswer on every question) - a guest/player hitting
// these URLs directly gets the player-safe payload instead and the template
// blows up on the missing fields. Without a session they go to Login (and the
// requested URL is restored once a staff session appears); a logged-in
// non-staff user goes back to Home.

// A GUEST has no persisted user id - the server can't attach an activity
// answer to one (see update_activity in server.js), so a guest reaching these
// routes directly (e.g. by URL) must be bounced before they can log in via
// Register/Login first.
const ONLINE_PLAYER_ROUTES = ['MySeasons', 'MyStages', 'StageActivity'];
router.beforeEach(async (to, from, next) => {
    if(to.meta && to.meta.admin){
        const user = window.store && window.store.state.game.user;
        const role = user ? user.role : null;
        const is_staff = role === utils.USER_ROLES.ADMINISTRATOR || role === utils.USER_ROLES.ASSISTANT;
        if(!is_staff){
            // Already logged in as a non-staff role (player/manager) - the
            // panel isn't for them, same as before.
            if(role && role !== utils.USER_ROLES.GUEST){
                next({name: 'Home'});
                return;
            }
            // No staff session (yet) - either not logged in, or a hard
            // refresh / new tab whose websocket session hasn't resumed yet.
            // Remember where they were going so handle_client() can take
            // them there once a staff session shows up (see
            // utils/admin-redirect.js).
            save_pending_admin_route(to.fullPath);
            next({name: 'Login'});
            return;
        }
    }
    if(ONLINE_PLAYER_ROUTES.includes(to.name)){
        // Online identity lives in state.online_user (see store/index.js),
        // not game.user - it's restored asynchronously on boot via
        // main.js's ensure_online_identity_checked (the REST equivalent of
        // the websocket's session-resume-by-CLIENT_JOIN). Await it before
        // deciding, so a hard refresh of a deep link doesn't get bounced to
        // Home while that restore is still in flight.
        if(window.ensure_online_identity_checked)
            await window.ensure_online_identity_checked;

        const online_user = window.store && window.store.state.online_user;
        if(!online_user){
            next({name: 'Home'});
            return;
        }
    }
    next();
});

router.afterEach((to, from, failure) => {
    window.store.commit("setCurrentView", to);
})

export default router
